const _ = require('lodash');
const { renameToInternalParams, rlvPrefix } = require('./constants');
const { hb: { SUM_DESCRIPTIONS } } = require('../reportData/constants');
const { NATIVE_FIELDS, NATIVE_IMAGE_FIELDS } = require('./native');
const EnvReportInterface = require('../reportData/envReportInterface');
const BuiltInPlacementFields = require('./builtInPlacementTagFields');
const BuiltInPbConfigTagFields = require('./builtInPbConfigTagFields');
const BuiltInSiteTagFields = require('./builtInSiteTagFields');
const PubxSiteTagFields = require('./pubxSiteTagFields');
const DpoPerformanceFilters = require('../misc/dpoPerformanceFilter');
const { FieldNames: DiFieldNames } = require('../dynamicInsert/constants');

const getDiHideList = () => {
	const diEnabled = EnvReportInterface.getGenericData().dynamicInsertEnabled;
	if (diEnabled) {
		return (f) => f;
	}
	return (f) => (DiFieldNames.includes(f.name) ? { ...f, shouldHide: () => true } : f);
};

const splitToDesc = (str) => {
	let res = str;
	for (let i = res.length - 1; i >= 0; i -= 1) {
		const c = res.charCodeAt(i);
		if ((c >= 65 && c <= 90) || (c >= 48 && c <= 57)) {
			res = `${res.slice(0, i)} ${res.slice(i)}`;
		}
	}
	return res[0].toUpperCase() + res.slice(1);
};

const sharedVideoFields = [
	{
		type: 'Object',
		name: 'playerSize',
		description: 'Player size',
		fields: [
			{
				type: 'Number',
				name: 'width',
				description: 'Width',
				defaultValue: 1,
				isRequired: true,
			},
			{
				type: 'Number',
				name: 'height',
				description: 'Height',
				defaultValue: 1,
				isRequired: true,
			},
		],
	},
	{
		type: 'Number',
		name: 'minduration',
		description: 'Min duration',
		defaultValue: 3,
	},
	{
		type: 'Boolean',
		name: 'skip',
		description: 'Skippable',
		defaultValue: false,
	},
	{
		type: 'String',
		name: 'playbackmethod',
		description: 'Playback method',
		hasOptions: true,
		isRequired: true,
		defaultValue: '2',
		options: [
			{
				name: '1',
				label: 'Initiate on page load with sound on',
			},
			{
				name: '2',
				label: 'Initiate on page load with sound off by default',
			},
			{
				name: '3',
				label: 'Initiate on click with sound on',
			},
			{
				name: '4',
				label: 'Initiate on mouse over with sound on',
			},
			{
				name: '5',
				label: 'Initiate on entering viewport with sound on',
			},
			{
				name: '6',
				label: 'Initiate on entering viewport with sound off by default',
			},
		],
	},
];

const PrebidDataObjects = {
	generalSettings: {
		name: 'General settings',
		availForNode(node) {
			return !(node && node.isBidderNode);
		},
		getTagFields() {
			return [
				{
					type: 'Boolean',
					name: 'mobileEnabled',
					description: 'Enable Mobile',
					defaultValue: false,
				},
				{
					type: 'Boolean',
					name: 'ampEnabled',
					description: 'Enable AMP',
					defaultValue: false,
				},
			];
		},
		excludeCustomParameters: true,
	},
	genericPlacementData: {
		name: 'Generic Placement Data',
		availForNode(node) {
			return !(node && node.isBidderNode);
		},
		getTagFields({ globalSettings }) {
			const hideIfDisabled = getDiHideList();
			return [
				...globalSettings.placementTag.fields,
				...BuiltInPlacementFields.map((f) => hideIfDisabled(f)),
			];
		},
	},
	genericPbConfigData: {
		name: 'Generic Prebid Config Data',
		availForNode(node, pbConfig, dstObj) {
			return !pbConfig
				&& !node?.getParentOfType('PlacementNode', true)
				&& dstObj?.constructor?.clsName?.() !== 'PlacementType';
		},
		getTagFields({ globalSettings }) {
			const hideIfDisabled = getDiHideList();
			return [
				...globalSettings.pbConfigTag.fields,
				...BuiltInPbConfigTagFields.map((f) => hideIfDisabled(f)),
				{
					type: 'Number',
					name: 'rlvLowPerfSample',
					description: 'Sample rate % for low performing SSPs',
					isRequired: true,
					defaultValue: 5,
					props: {
						between: { low: 0, high: 100 },
						help: 'Only needed when Demand Path Optimization is enabled',
					},
				},
				{
					name: 'rlvBySsp',
					description: 'Demand Path Optimization',
					byObjectType: 'BaseSsp',
					type: 'ByObject',
					omitWhenDefault: true,
					fields: [
						{
							name: 'filterType',
							description: 'How to filter',
							defaultValue: null,
							hasOptions: true,
							uiGroup: 'Country filter',
							multiSelect: false,
							omitWhenDefault: true,
							options: [
								{
									name: 'exclude',
									label: 'Exclude',
								},
								{
									name: 'include',
									label: 'Include',
								},
							],
							type: 'String',
						},
						{
							name: 'countries',
							description: 'What to filter',
							hasOptions: true,
							optionType: 'Country',
							uiGroup: 'Country filter',
							multiSelect: true,
							type: 'String',
							defaultValue: [],
							omitWhenDefault: true,
						},
						{
							name: 'perfMethod',
							description: 'Metric of interest',
							hasOptions: true,
							uiGroup: 'Performance Filter',
							defaultValue: DpoPerformanceFilters.DefaultPerfMethod,
							isRequired: true,
							omitWhenDefault: true,
							removeFromJs: true,
							type: 'String',
							options: _.entries(_.pick(SUM_DESCRIPTIONS, DpoPerformanceFilters.AvailablePerfMethods))
								.map(([name, label]) => ({ name, label })),
						},
						{
							name: 'perfValue',
							description: 'Threshold value',
							uiGroup: 'Performance Filter',
							renderData: 'renderDpoFilterValue',
							type: 'Number',
							defaultValue: null,
							omitWhenDefault: true,
							removeFromJs: true,
						},
						{
							name: 'byCountry',
							description: 'Decide by country',
							props: {
								help: 'Recommended only for sites with a substantial spread of countries among their visitors, as this option might require an extra api-call to be made - slightly delaying the start of the first auction for a page view.',
							},
							uiGroup: 'Performance Filter',
							type: 'Boolean',
							defaultValue: false,
							omitWhenDefault: true,
							removeFromJs: true,
						},
					],
				},
			];
		},
	},
	genericSiteData: {
		name: 'Generic Site Data',
		availForNode(...args) {
			return PrebidDataObjects.genericPbConfigData.availForNode(...args);
		},
		getTagFields({ globalSettings }) {
			return [
				...globalSettings.siteTag.fields,
				...BuiltInSiteTagFields,
				...(EnvReportInterface.getGenericData().pubxEnabled ? PubxSiteTagFields : []),
			];
		},
	},

	outstreamVideoSettings: {
		name: 'Outstream video settings',
		availForNode(node) {
			return !(node && node.isBidderNode);
		},

		getTagFields() {
			return [
				{
					type: 'String',
					name: 'mediaType',
					description: 'Media type',
					hasOptions: true,
					defaultValue: null,
					options: [
						{
							name: 'outstream',
							label: 'Outstream',
						},
						{
							name: 'bannerOutstream',
							label: 'Banner + Outstream',
						},
					],
				},
				...sharedVideoFields,
				{
					type: 'String',
					name: 'placement',
					description: 'Placement of video',
					defaultValue: '2',
					hasOptions: true,
					isRequired: true,
					options: [
						['2', 'In-banner'],
						['3', 'In-article'],
						['4', 'In-feed'],
						['5', 'Interstitial/Floating'],
					].map(([name, label]) => ({ name, label })),
				},
				{
					type: 'String',
					name: 'plcmt',
					description: 'Placement of video (OpenRTB 2.6 \'plcmt\')',
					defaultValue: '4',
					hasOptions: true,
					isRequired: true,
					options: [
						['3', 'Interstitial'],
						['4', 'No Content/Standalone'],
					].map(([name, label]) => ({ name, label })),
				},
				{
					type: 'Number',
					name: 'maxduration',
					description: 'Max duration',
					defaultValue: 3600,
				},
				{
					type: 'String',
					name: 'adText',
					description: 'Ad text',
					defaultValue: 'Ad',
				},
				{
					type: 'String',
					name: 'skipText',
					description: 'Skip text',
					defaultValue: 'Video can be skipped in %%TIME%% seconds',
				},
				{
					type: 'String',
					name: 'skipButtonText',
					description: 'Skip button text',
					defaultValue: 'Skip',
				},
				{
					type: 'Boolean',
					name: 'allowOverride',
					description: 'Allow VAST to override player skip settings',
					defaultValue: false,
				},
				{
					type: 'Number',
					name: 'expandTime',
					description: 'Expand time (ms)',
					defaultValue: 0, // i.e expand disabled
				},
				{
					type: 'Boolean',
					name: 'enableMaxHeight',
					description: 'Enable max height',
					defaultValue: false,
				},
				{
					// Essentially maps to disableCollapse option for appnexus renderer settings
					type: 'Object',
					name: 'endBehavior',
					description: 'End behavior',
					defaultValue: {},
					fields: [
						{
							type: 'Boolean',
							name: 'collapse',
							description: 'Collapse',
							defaultValue: false,
						},
						{
							type: 'Boolean',
							name: 'replay',
							description: 'Replay',
							defaultValue: false,
						},
						{
							type: 'Boolean',
							name: 'hideControlBar',
							description: 'Hide control bar',
							defaultValue: false,
						},
					],
				},
				{
					type: 'Boolean',
					name: 'forceBannerAdserverTargeting',
					description: 'Force banner adserver targeting',
					defaultValue: false,
				},
			];
		},
	},

	instreamVideoSettings: {
		name: 'Instream video settings',
		availForNode(node) {
			return !(node && node.isBidderNode);
		},
		getTagFields() {
			return [
				...sharedVideoFields,
				{
					type: 'String',
					name: 'plcmt',
					description: 'Placement of video (OpenRTB 2.6 \'plcmt\')',
					defaultValue: '1',
					hasOptions: true,
					isRequired: true,
					options: [
						['1', 'Instream'],
						['2', 'Accompanying Content'],
					].map(([name, label]) => ({ name, label })),
				},
				{
					type: 'Object',
					name: 'api',
					description: 'Supported APIs',
					defaultValue: {},
					fields: [
						{
							type: 'Boolean',
							name: '1',
							description: 'VPAID 1.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '2',
							description: 'VPAID 2.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '3',
							description: 'MRAID 1.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '4',
							description: 'ORMMA',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '5',
							description: 'MRAID 2.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '6',
							description: 'MRAID 3.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '7',
							description: 'OMID 1.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '8',
							description: 'SIMID 1.0',
							defaultValue: true,
						},

					],
				},
				{
					type: 'Array',
					name: 'mimes',
					description: 'Supported MIME types',
					defaultValue: ['video/mp4', 'video/webm', 'video/ogg', 'application/javascript'].map((s) => ({
						mime: s,
					})),
					fields: [{
						type: 'String',
						name: 'mime',
						description: 'Mime type',
						defaultValue: '',
					}],
				},
				{
					type: 'Object',
					name: 'protocols',
					description: 'Supported video protocols',
					defaultValue: {},
					fields: [
						{
							type: 'Boolean',
							name: '1',
							description: 'VAST 1.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '2',
							description: 'VAST 2.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '3',
							description: 'VAST 3.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '4',
							description: 'VAST 1.0 Wrapper',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '5',
							description: 'VAST 2.0 Wrapper',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '6',
							description: 'VAST 3.0 Wrapper',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '7',
							description: 'VAST 4.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '8',
							description: 'VAST 4.0 Wrapper',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '9',
							description: 'DAAST 1.0',
							defaultValue: true,
						},
						{
							type: 'Boolean',
							name: '10',
							description: 'DAAST 1.0 Wrapper',
							defaultValue: true,
						},

					],
				},
				{
					type: 'Number',
					name: 'maxduration',
					description: 'Max duration',
					defaultValue: 60,
				},
				{
					type: 'Boolean',
					name: 'hbaInject',
					omitWhenDefault: true,
					description: 'Inject HBA <Impression> tags in VAST (Web only)',
					props: {
						help: 'This acts as an alternative to tracking impressions via e.g. Google IMA SDK, using our built-in settings. It is ok to register impressions in HBA using multiple methods - as an impression for the same bid will still just be counted once.',
					},
					defaultValue: 60,
				},
			];
		},
	},
	nativeSettings: {
		name: 'Native settings',
		availForNode(node) {
			return !(node && node.isBidderNode);
		},
		getTagFields() {
			const res = [
				{
					type: 'String',
					name: 'nativeMode',
					description: 'Mode',
					defaultValue: 'disabled',
					hasOptions: true,
					isRequired: true,
					options: [
						{
							name: 'disabled',
							label: 'Disabled',
						},
						{
							name: 'enabled',
							label: 'Enabled',
						},
						{
							name: 'exclusive',
							label: 'Native only (will disable banner format)',
						},
					],
				},
				{
					type: 'String',
					name: 'ortbMode',
					description: 'ORTB mode for web',
					hasOptions: true,
					isRequired: true,
					defaultValue: 'legacy',
					options: [
						{
							name: 'legacy',
							label: 'Legacy format',
						},
						{
							name: 'legacyOrtb',
							label: 'Legacy format with ORTB (recommended)',
						},
						{
							name: 'ortb',
							label: 'ORTB only',
						},
					],
				},
				{
					type: 'Object',
					name: 'ortb',
					description: 'ORTB settings',
					shouldHide: (ctx, field, model) => (
						!['legacyOrtb', 'ortb'].includes(model[rlvPrefix('ortbMode')].value)
					),
					fields: [
						{
							type: 'String',
							name: 'context',
							description: 'Context type (context)',
							hasOptions: true,
							isRequired: true,
							defaultValue: '1',
							options: [
								{
									name: '1',
									label: 'Content-centric (1)',
								},
								{
									name: '2',
									label: 'Social-centric (2)',
								},
								{
									name: '3',
									label: 'Product context (3)',
								},
							],
						},
						{
							type: 'String',
							name: 'plcmttype',
							description: 'Placement type (plcmttype)',
							hasOptions: true,
							isRequired: true,
							defaultValue: '1',
							options: [
								{
									name: '1',
									label: 'In the feed of content (1)',
								},
								{
									name: '2',
									label: 'In the atomic unit of the content (2)',
								},
								{
									name: '3',
									label: 'Outside the core content (3)',
								},
								{
									name: '3',
									label: 'Recommendation widget (4)',
								},
							],
						},
					],
				},
				{
					type: 'HTML',
					name: 'adTemplate',
					description: 'Ad Template',
				},
				{
					type: 'String',
					name: 'rendererUrl',
					description: 'Renderer URL',
				},
				{
					type: 'Boolean',
					name: 'bannerTarg',
					isNativeInfo: true,
					description: 'Use hb_format=banner when customNativeRender is used for rendering',
					props: {
						help: 'When implementing custom native rendering using the customNativeRender callback, the prebid creative must use the normal banner format. For e.g. newer versions of the Prebid Universal Creative, containg "%%PATTERN:hb_format%%.js" - this setting must be enabled for rendering to work',
					},
					omitWhenDefault: true,
				},
				{
					type: 'Boolean',
					name: 'sendTargetingKeys',
					description: 'Enable "sendTargetingKeys" (only for legacy line-item setups)',
				},
				...NATIVE_FIELDS.map((fld) => ({
					type: 'Object',
					name: fld,
					description: fld === 'cta' ? 'Call to Action' : splitToDesc(fld),
					fields: [
						{
							type: 'Boolean',
							name: 'disabled',
							description: 'Disable asset',
						},
						{
							type: 'Boolean',
							name: 'required',
							description: 'Required',
						},
						...(NATIVE_IMAGE_FIELDS.includes(fld) ? [
							{
								type: 'Number',
								name: 'width',
								description: 'Width',
							},
							{
								type: 'Number',
								name: 'height',
								description: 'Height',
							},
							{
								type: 'Boolean',
								name: 'useAspec',
								description: 'Allow larger images with same aspect ratio',
							},
						] : [
							{
								type: 'Number',
								name: 'length',
								description: 'Length',
							},
						]),
					],
				})),
			];
			return renameToInternalParams(res);
		},
	},
};

module.exports = { PrebidDataObjects };
